<template>
  <v-app>
    <CompositeEssentials :hasHeader="true" />
    <SystemMessage :systemMessage="systemMessage" />
    <CookieMessage />
    <v-main>
      <div id="header" class="header" :data-adacado-dataset="headerDataset" style="height:0">
        <div v-pre></div>
      </div>
      <div
        v-if="!$firebaseUser || (isMobileSized && mobileCompleteContent != 'analytics')"
        class="unAuthLogo"
        :class="{ mobileLogo: isMobileSized }"
      >
        <img
          :class="{ adacadoLogoMobile: isMobileSized }"
          src="https://storage.googleapis.com/adacado-logos/adacadoLogo.svg"
        />
      </div>
      <v-container pa-0 ma-0 style="background: white;" ref="quickStartContainer">
        <template v-if="!isProductionEnv || (isProductionEnv && !showBrowserWall)">
          <!-- hidden content required to prevent bug with vue-transition -->
          <!-- Login Form -->
          <div
            v-if="!$firebaseUser"
            class="font-weight-bold"
            style="position:fixed; right: 35px; top: 20px; z-index: 3;cursor:pointer;"
            :style="{
              color: isMobileSized ? 'rgb(94, 0, 235)' : 'white',
              marginTop: isMobileSized ? '22px' : '0px',
            }"
            @click="
              guibus_publish('dispatch:beacon', {
                eventId: `welcome-app_main_login_button-click${isMobileSized ? '-mobile' : ''}`,
              })
              loginModalIsOpen = true
            "
            >Login</div
          >
          <transition name="fadeInSlideUp" mode="out-in" appear>
            <div>
              <Intro
                v-if="currentStep === 'intro'"
                :isMobileSized="isMobileSized"
                @changeStep="changeStep"
              />
            </div>
          </transition>
          <Survey
            v-if="currentStep === 'survey'"
            :currentSurveyData="surveyData"
            :isQuickStart="isQuickStart"
            :isMobileSized="isMobileSized"
            @changeStep="handleSurveyAndContinue"
            @stepBack="changeStep"
          />
          <Solutions
            v-if="currentStep === 'solutions'"
            :vertical="selectedVertical"
            :subVertical="selectedSubVertical"
            :useCase="selectedUseCase"
            :mode="selectedAdType"
            :solutionId="selectedSolutionId"
            :isShopifyUser="isShopifyUser"
            :isQuickStart="isQuickStart"
            :isMobileSized="isMobileSized"
            @changeMode="changeMode"
            @changeStep="handleSolutionAndContinue"
            @stepBack="changeStep"
          />
          <Ads
            v-if="currentStep === 'ads'"
            :solutionId="selectedSolutionId"
            :vertical="selectedVertical"
            :subVertical="selectedSubVertical"
            :useCase="selectedUseCase"
            :mode="selectedAdType"
            :isShopifyUser="isShopifyUser"
            :dataFeedSettingsProps="dataFeedSettings"
            :inventoryItemsProps="inventoryItems"
            :storyboardsProps="storyboards"
            :frameCountProps="frameCount"
            :isQuickStart="isQuickStart"
            :isMobileSized="isMobileSized"
            @handleCreatives="handleCreatives"
            @changeMode="changeMode"
            @changeStep="changeStep"
          />
          <Advertiser
            v-if="currentStep === 'advertiser'"
            :vertical="selectedVertical"
            :subVertical="selectedSubVertical"
            :isShopifyUser="isShopifyUser"
            :currentAdvertiserData="advertiserData"
            :schedule="schedule"
            :currentCartography="selectedCartography"
            :isQuickStart="isQuickStart"
            :isMobileSized="isMobileSized"
            @handleAdvertiserData="handleAdvertiserData"
            @changeStep="changeStep"
          />
          <Launch
            v-if="currentStep === 'launch'"
            :budgetingSchedule="budgetingSchedule"
            :storyboardsProps="storyboards"
            :inventoryItemsProps="inventoryItems"
            :isAgency="selectedAdvertiserType === 'agency'"
            :isQuickStart="isQuickStart"
            :advertiserData="advertiserData"
            :isMobileSized="isMobileSized"
            :mode="selectedAdType"
            @stepBack="changeStep"
            @save="doSave"
            @setSystemMessage="setSystemMessage"
          />
          <!-- Saving Indicator -->
          <SaveIndicator
            v-if="currentStep === 'saving'"
            :currentProgress="currentProgress"
            :progressText="progressText"
            :color="progressIndicatorColor"
          />
          <!-- Mobile Complete -->
          <MobileComplete
            v-if="currentStep === 'mobileComplete'"
            :advertiserId="newAdvertiserId"
            :campaignId="newCampaignId"
            :currentView="mobileCompleteContent"
            @handleServeMyAds="handleServeMyAds"
          />
        </template>
        <template v-else class="mt-7">
          <!-- inform user when they are using unavailable browsers -->
          <transition name="fadeInSlideUp" mode="out-in" appear>
            <div class="welcomeContainer">
              <div class="welcomeContent">
                <div class="welcomeBox">
                  <div class="welcomeHeader">
                    <div class="text-h1 welcomeTitle">
                      Welcome
                    </div>
                    <div class="text-h1 welcomeTitle">
                      Back
                    </div>
                  </div>
                  <v-card class="panel pa-6">
                    <p class="text-h5 my-4">Excuse us...</p>
                    <p class="text-h6"
                      >We see you are using {{ currentBrowserName | capitalize }} version
                      {{ currentBrowserVersion }}. To provide the best level of security we require
                      our users to use only the latest Chrome, Safari, Firefox, or Edge browsers. We
                      are working to make Adacado available on more platforms, and if your preferred
                      browser isn't on this list please send us a note in the chat below and we can
                      update you when support for your browser is ready.</p
                    >
                    <p class="text-h6"
                      ><a
                        href="https://www.google.com/chrome/"
                        target="_blank"
                        @click="
                          guibus_publish('dispatch:beacon', {
                            eventId: 'welcome-app_main_get_chrome_link-click',
                          })
                        "
                        >Get Latest Chrome</a
                      ></p
                    >
                    <p class="text-h6"
                      ><a
                        href="https://www.mozilla.org/firefox/"
                        target="_blank"
                        @click="
                          guibus_publish('dispatch:beacon', {
                            eventId: 'welcome-app_main_get_firefox_link-click',
                          })
                        "
                        >Get Latest Firefox</a
                      ></p
                    >
                    <p class="text-h6"
                      ><a
                        href="https://www.microsoft.com/edge"
                        target="_blank"
                        @click="
                          guibus_publish('dispatch:beacon', {
                            eventId: 'welcome-app_main_get_edge_link-click',
                          })
                        "
                        >Get Latest Edge</a
                      >
                    </p>
                  </v-card>
                </div>
              </div>
            </div>
          </transition>
        </template>
      </v-container>
      <div
        v-if="!$firebaseUser"
        class="caption footerWrapper"
        :class="{
          'my-2': !isMobileSized,
          mobileFooter: isMobileSized && currentStep !== 'mobileComplete',
          mobileCompleteFooter: currentStep === 'mobileComplete',
        }"
      >
        <!-- class "adacadoTerms" is used in another file (./components/slides/Ads.vue) -->
        <div class="adacadoTerms" :class="{ mobilePrivacyDisplay: isMobileSized }">
          {{ currentYear }} Adacado All Rights Reserved
          <a
            href="https://www.adacado.com/privacy-policy/"
            target="_blank"
            @click="
              guibus_publish('dispatch:beacon', {
                eventId: 'welcome-app_main_cookie_preferences_link-click',
              })
            "
            >Cookie Preferences</a
          >,
          <a
            href="https://www.adacado.com/privacy-policy/"
            target="_blank"
            @click="
              guibus_publish('dispatch:beacon', {
                eventId: 'welcome-app_main_get_privacy_policy_link-click',
              })
            "
            >Privacy</a
          >, and
          <a
            href="https://adacado.com/support/terms-of-service/"
            target="_blank"
            @click="
              guibus_publish('dispatch:beacon', {
                eventId: 'welcome-app_main_terms_link-click',
              })
            "
            >Terms</a
          >
        </div>
      </div>
    </v-main>
    <LoginForm
      :loginModalIsOpen="loginModalIsOpen"
      :isMobileSized="isMobileSized"
      @modalClosed="loginModalIsOpen = false"
      @setSystemMessage="setSystemMessage"
    />
    <ResumeProgress
      :modalIsOpen="resumeProgressModal"
      :isMobileSized="isMobileSized"
      @resumeProgress="handleResumeProgressModalAction"
    />
  </v-app>
</template>

<script>
import env from 'env-service'
import { detect } from 'detect-browser'
import AssetPathService from 'asset-path-service'
import CompositeEssentials from 'component-composite-essentials'
import CookieMessage from 'component-cookie-message'
import Parameter from 'parameter-mixin'
import SystemMessage from 'component-system-message'
import CanvasExporter from 'template-html-generator-service/CanvasExporter'
import FemsService from 'fems-service'
import Intro from './components/slides/Intro'
import Survey from './components/slides/Survey'
import Solutions from './components/slides/Solutions'
import Ads from './components/slides/Ads'
import Advertiser from './components/slides/Advertiser.vue'
import Launch from './components/slides/Launch'
import LoginForm from './components/modals/LoginForm'
import ResumeProgress from './components/modals/ResumeProgress'
import SaveIndicator from './components/slides/SaveIndicator'
import MobileComplete from './components/slides/MobileComplete'
import {
  DEFAULT_BUDGET_CONFIG,
  DEFAULT_AUDIENCE_CONFIG,
} from './components/configs/defaultMediaChannelConfigs'
import ApplicationConfig from 'application-config'
import DatafeedInterfaceFactoryNoAuth from 'bs-interface-datafeed/bs-interface-datafeed-prelogin'
import AdvertiserMerchantInterfaceFactoryNoAuth from 'bs-interface-advertiser-merchant/bs-interface-advertiser-merchant-prelogin'

export default {
  name: 'Welcome',
  mixins: [Parameter],
  components: {
    CompositeEssentials,
    SystemMessage,
    Intro,
    Survey,
    Solutions,
    Ads,
    Advertiser,
    Launch,
    SaveIndicator,
    CookieMessage,
    LoginForm,
    ResumeProgress,
    MobileComplete,
  },
  data() {
    return {
      // steps: ['intro', 'survey', 'solutions', 'ads', 'advertiser', 'launch', 'mobileComplete']
      currentStep: '',
      mobileCompleteContent: 'analytics',
      localStorageData: {},
      accounts: JSON.parse(localStorage.getItem('accounts')) || {},
      systemMessage: {
        isShownImmediately: false,
        type: 'success',
        message: '',
      },
      mode: undefined,
      isQuickStart: false,
      optimizations: undefined,
      isShopifyUser: false,
      isProductionEnv: env.IS_PRODUCTION_CONNECTION,
      showBrowserWall: false,
      currentBrowserName: undefined,
      currentBroswerVersion: undefined,

      loginModalIsOpen: false,

      resumeProgressModal: false,
      resumeProgressAction: undefined,
      resumeProgressModal_interruptResolveFunction: undefined,
      isResumingProgress: false,

      // save process data
      currentProgress: 0,
      progressText: 'Creating Advertiser...',
      progressIndicatorColor: 'blue darken-2',
      newAdvertiserId: undefined,
      newCampaignId: undefined,

      // survey slide
      selectedVertical: undefined,
      selectedAdvertiserType: undefined,
      selectedAdType: undefined,
      selectedSubVertical: undefined,
      selectedUseCase: undefined,
      surveyData: undefined,

      // solutions slide
      selectedSolutionId: undefined,

      // ads slide
      dataFeedSettings: undefined,
      inventoryItems: undefined,
      storyboards: undefined,
      frameCount: 1,

      // advertiser slide
      advertiserData: undefined,
      schedule: undefined,
      selectedCartography: undefined,
      budgetingSchedule: undefined,

      // launch slide
      newAdvertiserData: undefined,

      shopifyDefaultDataFeedSettings: {
        feedAccess: {
          username: '',
          password: '',
          url: '',
          host: '',
          type: 'shopify',
          filePath: '',
        },
        dataFeedCSVParserVersion: 2,
        delimiter: ',',
        containsHeader: true,
        headers: [
          {
            headerColumnName: 'id',
            internalMapping: 'productId',
          },
          {
            headerColumnName: 'name',
            internalMapping: 'productTitle',
          },
          {
            headerColumnName: 'description',
            internalMapping: 'description',
          },
          {
            headerColumnName: 'price',
            internalMapping: 'price',
          },
          {
            headerColumnName: 'quantity',
            internalMapping: 'availability',
          },
          {
            headerColumnName: 'currency_code',
            internalMapping: 'currencySymbol',
          },
          {
            headerColumnName: 'image',
            internalMapping: 'productImage',
          },
        ],
        alertLevel: -1,
        feedSchedule: '25 05 * * *',
        feedScheduleOneOff: '',
        feedType: 'shopify',
        isManualDelimiter: false,
        shouldProcess: true,
      },
    }
  },
  async beforeMount() {
    window.oncontextmenu = function(event) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }
    const browser = detect()
    this.currentBrowserName = browser.name
    this.currentBrowserVersion = parseInt(browser.version.split('.')[0])
    this.showBrowserWall =
      !['chrome', 'firefox', 'edge-chromium', 'safari'].includes(browser.name) &&
      !this.isMobileSized
    // hide hint sidebar (not used here)
    if (document.getElementsByClassName('hintSideBar')[0]) {
      document.getElementsByClassName('hintSideBar')[0].style.display = 'none'
    }

    const mode = this.$getAdacadoParams(['MODE']).error
      ? undefined
      : this.$getAdacadoParams(['MODE']).MODE

    this.isQuickStart = this.$firebaseUser && mode === 'quickstart'
    switch (true) {
      case this.isQuickStart:
        this.currentStep = 'survey'
        break
      default:
        this.currentStep = 'intro'
    }
    // render header page if logged in
    if (this.isQuickStart) {
      FemsService.renderChildFems('header', 'v2')
    } else if (!this.$firebaseUser) {
      this.localStorageData = JSON.parse(localStorage.getItem('adaQuickStartData') || '{}')
      const initialVisit = localStorage.getItem('adaInitialVisit')
      if (!initialVisit) {
        localStorage.setItem('adaInitialVisit', new Date())
      }
      // read the localstorage value for last visit, if < 3 days, show the "Pick up where you left off?" modal
      const lastVisit = localStorage.getItem('adaLastVisit')
      if (lastVisit) {
        const currentDate = new Date().getTime()
        const diffTime = Math.abs(currentDate - lastVisit)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        if (
          diffDays < 3 &&
          this.localStorageData.currentStep &&
          this.localStorageData.currentStep !== 'intro'
        ) {
          if (await this.showResumeProgressModal()) {
            this.localStorageDataSetup()
          } else {
            // start over, clear data
            localStorage.removeItem('adaQuickStartData')
          }
        } else {
          localStorage.removeItem('adaQuickStartData')
        }
      }
      localStorage.setItem('adaLastVisit', new Date().getTime())
    }
    // if not mobile sized, then add the chatButton css to the header style tag
    if (!this.isMobileSized) {
      const style = document.createElement('style')
      style.innerHTML = `
        .chatButton {
          background-color: white !important;
          border-color: white !important;
        }
        .chatButton i {
          color: rgb(94, 0, 235) !important;
        }
      `
      document.head.appendChild(style)
    }
    if (this.isMobileSized && this.$firebaseUser) {
      // if mobile && logged in, push user to the mobileComplete view
      this.newAdvertiserId = this.accounts[this.$firebaseUser.email]?.currentAdvertiserId
      this.newCampaignId = this.accounts[this.$firebaseUser.email]?.currentCampaignId
      let advertisersList
      // if either IDs are missing, go through existing advertisers and campaigns to find one
      if (!this.newAdvertiserId || !this.newCampaignId) {
        await this.AdvertiserInterface.getAll(advertisers => {
          if (advertisers.length === 0) {
            // logout if account has no advertisers
            window.location = '/campaign-manager/v1/page'
          } else {
            advertisersList = advertisers
          }
        })
        let idx = 0
        // loop through advertisers until one with a campaign is found
        while (idx < advertisersList.length) {
          await this.CampaignInterface.getList(advertisersList[idx].AdvertiserID, campaigns => {
            if (campaigns.length) {
              this.newCampaignId = campaigns[0].CampaignID
            }
          })
          // if found, exit loop
          if (this.newCampaignId) {
            this.newAdvertiserId = advertisersList[idx].AdvertiserID
            break
          }
          idx++
        }
        // if either ID is still missing, logout
        if (!this.newAdvertiserId || !this.newCampaignId) {
          window.location = '/campaign-manager/v1/page'
        } else {
          // save the advertiser and campaign IDs to local storage
          if (!this.accounts[this.$firebaseUser.email]) {
            this.accounts[this.$firebaseUser.email] = {}
          }
          this.accounts[this.$firebaseUser.email].currentAdvertiserId = this.newAdvertiserId
          this.accounts[this.$firebaseUser.email].currentCampaignId = this.newCampaignId
          localStorage.setItem('accounts', JSON.stringify(this.accounts))
        }
      }
      if (localStorage.getItem('adaIsRedirectToAdTagsFromOnBoarding')) {
        this.mobileCompleteContent = 'adTags'
      } else if (localStorage.getItem('adaIsRedirectToAdServingFromQuickStart')) {
        this.mobileCompleteContent = 'saveAndContinue'
      } else {
        // only the analytics view should have the header
        FemsService.renderChildFems('header', 'v2')
      }
      this.currentStep = 'mobileComplete'
      window.addEventListener('resize', this.adjustFooterPosition)
    }
  },
  watch: {
    currentStep() {
      window.scrollTo(0, 0)
    },
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    headerDataset() {
      return this.$constructAdacadoDataset({
        isQuickStart: !this.isMobileSized,
        isMobile: this.isMobileSized,
      })
    },
    AccountInterface() {
      return window.adacado.bscs ? window.adacado.bscs.AccountInterfaceFactory() : undefined
    },
    AdvertiserInterface() {
      return window.adacado.bscs ? window.adacado.bscs.AdvertiserInterfaceFactory() : undefined
    },
    AdvertiserMerchantInterface() {
      return window.adacado.bscs
        ? window.adacado.bscs.AdvertiserMerchantInterfaceFactory()
        : undefined
    },
    BillingAccountInterface() {
      return window.adacado.bscs ? window.adacado.bscs.BillingAccountInterfaceFactory() : undefined
    },
    CampaignInterface() {
      return window.adacado.bscs ? window.adacado.bscs.CampaignInterfaceFactory() : undefined
    },
    CreativeInterface() {
      return window.adacado.bscs ? window.adacado.bscs.CreativeInterfaceFactory() : undefined
    },
    CampaignSolutionInterface() {
      return window.adacado.bscs
        ? window.adacado.bscs.CampaignSolutionInterfaceFactory()
        : undefined
    },
    DatafeedInterface() {
      return window.adacado.bscs ? window.adacado.bscs.DatafeedInterfaceFactory() : undefined
    },
    DatafeedInterfaceNoAuth() {
      return DatafeedInterfaceFactoryNoAuth()
    },
    AdvertiserMerchantInterfaceNoAuth() {
      return AdvertiserMerchantInterfaceFactoryNoAuth()
    },
    MediaChannelInterface() {
      return window.adacado.bscs ? window.adacado.bscs.MediaChannelInterfaceFactory() : undefined
    },
    isMobileSized() {
      return window.innerWidth < 1000
    },
  },
  methods: {
    changeMode(adType) {
      this.selectedAdType = adType
      this.mode = this.selectedAdType
      this.resetCreativesData()
    },
    adjustFooterPosition() {
      var footer = document.querySelector('.footerWrapper.mobileFooter')
      var windowHeight = window.innerHeight
      var footerHeight = footer.offsetHeight
      var gap = windowHeight - footerHeight - window.pageYOffset
      if (gap > 0) {
        footer.style.bottom = gap + 'px'
      } else {
        footer.style.bottom = -8 + 'px'
      }
    },
    handleServeMyAds() {
      this.mobileCompleteContent = 'analytics'
      FemsService.renderChildFems('header', 'v2')
      this.guibus_subscribe('headerIsLoaded', this.handleHeaderLoaded)
      localStorage.removeItem('adaIsRedirectToAdTagsFromOnBoarding')
      localStorage.removeItem('adaIsRedirectToAdServingFromQuickStart')
    },
    handleHeaderLoaded() {
      this.guibus_publish('welcome-mobile-s&c:serveMyAds-clicked')
      this.guibus_unsubscribe('headerIsLoaded', this.handleHeaderLoaded)
    },
    async showResumeProgressModal() {
      await new Promise(res => {
        this.resumeProgressModal = true
        this.resumeProgressModal_interruptResolveFunction = res
      })
      // Act based on user's response to modal
      switch (this.resumeProgressAction) {
        case 'cancel':
        default:
          // User cancelled - abandon entire process
          return false
        case 'resume':
          // User opted to apply quick-fill data
          return true
      }
    },
    handleResumeProgressModalAction(val = 'cancel') {
      this.resumeProgressModal = false
      this.resumeProgressAction = val
      this.resumeProgressModal_interruptResolveFunction() // Resolve original promise that halted process for adding templates
    },
    localStorageDataSetup() {
      if (!this.localStorageData.currentStep) return
      this.currentStep = this.localStorageData.currentStep
      // intentional fallthrough, get the data for the current step and all previous steps
      /* eslint-disable no-fallthrough */
      switch (this.currentStep) {
        case 'launch':
        case 'advertiser':
          this.advertiserData = this.localStorageData.advertiser?.advertiserData
          this.schedule = this.localStorageData.advertiser?.schedule
          this.budgetingSchedule = this.localStorageData.advertiser?.budgetingSchedule
          this.selectedCartography = this.localStorageData.advertiser?.selectedCartography
        case 'ads':
          this.dataFeedSettings = this.localStorageData.ads?.dataFeedSettings
          this.inventoryItems = this.localStorageData.ads?.inventoryItems
          this.storyboards = this.localStorageData.ads?.storyboards
          this.frameCount = this.localStorageData.ads?.frameCount
          this.selectedSolutionId = this.localStorageData.solutions?.selectedSolutionId
        case 'solutions':
          this.selectedAdvertiserType = this.localStorageData.survey?.selectedAdvertiserType
          this.selectedVertical = this.localStorageData.survey?.selectedVertical
          this.selectedAdType = this.localStorageData.survey?.selectedAdType
          this.selectedSubVertical = this.localStorageData.survey?.selectedSubVertical
          this.selectedUseCase = this.localStorageData.survey?.selectedUseCase
          this.isShopifyUser = this.localStorageData.isShopifyUser
      }
      /* eslint-enable no-fallthrough */
    },
    handleSurveyAndContinue({ step, surveyData }) {
      this.selectedAdvertiserType = surveyData.advertiserType?.value
      this.selectedVertical = surveyData.vertical?.value
      this.selectedAdType = surveyData.adType?.value
      this.mode = this.selectedAdType
      this.selectedSubVertical =
        this.selectedVertical === 'service' ? surveyData.adType.name : undefined
      this.selectedUseCase =
        this.selectedVertical === 'service' ? surveyData.adType.name : surveyData.adType.logValue
      this.surveyData = surveyData
      this.localStorageData.survey = {
        selectedAdvertiserType: this.selectedAdvertiserType,
        selectedVertical: this.selectedVertical,
        selectedAdType: this.selectedAdType,
        selectedSubVertical: this.selectedSubVertical,
        selectedUseCase: this.selectedUseCase,
      }
      this.resetAdvertiserData()
      this.resetCreativesData()
      this.resetSolutionsData()
      this.changeStep(step)
      this.saveLocalStorageData()
    },
    handleSolutionAndContinue({ step, solutionId }) {
      this.selectedSolutionId = solutionId
      this.localStorageData.solutions = { selectedSolutionId: this.selectedSolutionId }
      this.resetCreativesData()
      this.changeStep(step)
      this.saveLocalStorageData()
    },
    handleCreatives(creativesData) {
      this.dataFeedSettings = creativesData.dataFeedSettings
      this.isShopifyUser = creativesData.dataFeedSettings?.feedType == 'shopify'
      this.inventoryItems = creativesData.inventoryItems
      this.storyboards = creativesData.storyboards
      this.frameCount = creativesData.frameCount
      this.localStorageData.ads = {
        dataFeedSettings: this.dataFeedSettings,
        inventoryItems: this.inventoryItems,
        storyboards: this.storyboards,
        frameCount: this.frameCount,
      }
      this.selectedSolutionId = creativesData.selectedSolutionId
      this.localStorageData.solutions = { selectedSolutionId: this.selectedSolutionId }
      this.saveLocalStorageData()
    },
    handleAdvertiserData(advertiserSlideData) {
      this.advertiserData = advertiserSlideData.advertiser
      this.schedule = advertiserSlideData.schedule
      this.budgetingSchedule = {
        StartDate: this.schedule.mediaChannelStartDate,
        EndDate: this.schedule.mediaChannelEndDate,
        ActivityConfig: JSON.parse(JSON.stringify(this.schedule.activityConfig)),
        BudgetConfig: JSON.parse(JSON.stringify(DEFAULT_BUDGET_CONFIG)),
      }
      this.selectedCartography = advertiserSlideData.selectedCartography
      this.localStorageData.advertiser = {
        advertiserData: this.advertiserData,
        schedule: this.schedule,
        budgetingSchedule: this.budgetingSchedule,
        selectedCartography: this.selectedCartography,
      }
      this.saveLocalStorageData()
    },
    saveLocalStorageData() {
      this.localStorageData.currentStep = this.currentStep
      localStorage.setItem('adaQuickStartData', JSON.stringify(this.localStorageData))
    },
    setSystemMessage(message) {
      this.systemMessage = message
    },
    changeStep(newValue) {
      this.currentStep = newValue
      if (newValue === 'advertiser' || newValue === 'launch') {
        this.saveLocalStorageData()
      }
    },
    resumeProgress() {
      this.resumeProgressModal = false
      this.localStorageData = JSON.parse(localStorage.getItem('adaQuickStartData') || '{}')
      this.localStorageDataSetup()
    },
    resetAdvertiserData() {
      this.advertiserData = undefined
      this.schedule = undefined
      this.budgetingSchedule = undefined
      this.selectedCartography = undefined
      this.localStorageData.advertiser = undefined
      this.saveLocalStorageData()
    },
    resetCreativesData() {
      this.dataFeedSettings = undefined
      this.inventoryItems = undefined
      this.storyboards = undefined
      this.frameCount = 1
      this.isShopifyUser = false
      this.localStorageData.ads = undefined
      this.saveLocalStorageData()
    },
    resetSolutionsData() {
      this.selectedSolutionId = undefined
      this.localStorageData.solutions = undefined
      this.saveLocalStorageData()
    },
    // create and save all data
    // 1. create advertiser
    // 2. get selected solution data to copy
    // 3. save campaign solution to advertiser
    // 4. get themes
    // 5. get storyboards
    // 6. get templateIds
    // 7. upload assets to all templates
    // 8. get templates
    // 9. update templates with new assets url
    // NOTE: currentProgress is rough estimate of progress, mainly for the user to see that something is happening while waiting
    // and also to indicate that the process is not stuck
    //
    // payload object:
    // {
    //   advertiser,
    //   solution,
    //   storyboards,
    //   geoSettings,
    //   budgetingAndSchedulingSettings,
    //   isActivatingMediaChannel,
    // }
    async doSave(payload) {
      this.currentStep = 'saving'
      const { hasValidPaymentInfo, isActivatingMediaChannel, isAdTagsUser, isOnboarding } = payload
      let selectedSolutionData, dspMediaChannel, themeIds
      let storyboardIds = []
      let templateIds = []
      let templateUpdatePromises = []

      // create advertiser
      await this.AdvertiserInterface.create(this.advertiserData).then(r => {
        this.newAdvertiserId = r.responseData.changedKeys[0].keyvalue[0]
        this.guibus_publish('newAdvertiserCreated', this.newAdvertiserId)
        this.currentProgress = 10
        if (isOnboarding && !isAdTagsUser && !isActivatingMediaChannel) {
          // save & continue
          this.progressText = 'Saving Progress...'
        } else {
          this.progressText = 'Generating Ads...'
        }
      })
      if (isOnboarding) {
        // update billing account with freemium advertiser
        this.BillingAccountInterface.updateFreemiumAdvertiserId({
          advertiserId: this.newAdvertiserId,
        })
      }
      const isSavingProductData =
        this.dataFeedSettings &&
        this.dataFeedSettings.feedType != '' &&
        this.dataFeedSettings.dataSource != 'notSet'
      if (isSavingProductData) {
        // apply and save datafeed settings to advertiser
        await this.AdvertiserInterface.get(this.newAdvertiserId, r => {
          this.newAdvertiserData = r
        })
        if (this.isShopifyUser) {
          await this.AdvertiserMerchantInterface.linkAdvertiserToShop(
            localStorage.getItem('shopifyConnectionCode'),
            this.newAdvertiserId
          ).then(() => {
            localStorage.removeItem('shopifyConnectionCode')
          })
          this.dataFeedSettings.maxProducts = -1
        } else {
          this.dataFeedSettings.maxProducts = hasValidPaymentInfo ? -1 : 5
        }
        this.dataFeedSettings.alertLevel = -1
        this.newAdvertiserData.Properties.dataSource = 'datafeed'
        this.newAdvertiserData.Properties.dataFeed = this.dataFeedSettings
        this.newAdvertiserData.Properties.dataFeed.shouldProcess = true
        await this.DatafeedInterface.updateAdvertiserDataFeed(this.newAdvertiserData)
      }
      // get selected solution data to copy
      await this.CampaignSolutionInterface.getCampaignSolution(this.selectedSolutionId, r => {
        this.currentProgress = 20
        selectedSolutionData = r
      })
      let mediaChannelsToUse = ['dspcentro']
      if (isAdTagsUser) {
        mediaChannelsToUse = ['adtags']
      }

      // save selected campaign solution to newly created advertiser
      await this.CampaignSolutionInterface.saveCampaignSolutionToAdvertiser({
        advertiserId: this.newAdvertiserId,
        campaignSolutionId: selectedSolutionData.campaignSolutionId,
        instructions: {
          isFreemium: isOnboarding,
          campaignName: selectedSolutionData.name,
          mediaChannels: {
            mediaChannelsToUse: mediaChannelsToUse,
            facebookToken: '',
          },
          datafeedConfig: {
            useFeed: false,
          },
        },
      }).then(r => {
        this.newCampaignId = r.responseData.changedKeys.find(
          key => key.keytype == 'cai'
        ).keyvalue[0]
        this.guibus_publish('newCampaignCreated', this.newCampaignId)
        this.currentProgress = 45
        this.progressText = 'Uploading Assets...'
      })
      if (isSavingProductData) {
        // update campaign datafeed settings
        let campaignData
        await this.CampaignInterface.get(this.newCampaignId, r => {
          campaignData = r
        })
        campaignData.Properties.VerticalFilters = {
          filter_groups: [
            {
              filter_parameters: [],
              operator: 'AND',
            },
          ],
          operator: 'AND',
        }
        await this.DatafeedInterface.updateCampaignDataFeed(campaignData)
      }
      let adTagsChannel = undefined
      if (isAdTagsUser) {
        // get adtags media channel
        await this.MediaChannelInterface.getMediaChannelListByCampaignID(
          this.newCampaignId,
          list => {
            adTagsChannel = list.find(mc => mc.type == 'adtags')
          }
        )
      } else {
        // get dsp media channel
        await this.MediaChannelInterface.getMediaChannelListByCampaignID(
          this.newCampaignId,
          list => {
            dspMediaChannel = list.find(mc => mc.type == 'dspcentro')
          }
        )
        // update media channel geo targeting
        this.MediaChannelInterface.updateGeoTargeting(dspMediaChannel.mediaChannelId, {
          geoTargeting: {
            SelectedOrigin: 'userConfigured',
            SelectedCartography: this.selectedCartography,
          },
        })
        // update media channel budgeting and scheduling
        this.MediaChannelInterface.updateBudgetingSchedule(dspMediaChannel.mediaChannelId, {
          BudgetingSchedule: {
            ...this.budgetingSchedule,
            Visited: true,
          },
        })
        // update media channel audience targeting
        this.MediaChannelInterface.updateAudience(dspMediaChannel.mediaChannelId, {
          audience: { ...DEFAULT_AUDIENCE_CONFIG, Visited: false },
        })
        if (isOnboarding) {
          let selectedCreatives = []
          dspMediaChannel.properties.SelectedCreatives[0].Creatives.forEach(creative => {
            if (creative.Format == '300x250' || creative.Format == '728x90') {
              selectedCreatives.push(creative)
            }
          })
          await this.MediaChannelInterface.updateSelectedCreatives(
            dspMediaChannel.mediaChannelId,
            selectedCreatives
          )
        }
      }
      this.currentProgress = 55

      // get all theme ids for new campaign
      await this.CreativeInterface.getThemeList(this.newCampaignId, ids => {
        themeIds = ids
      })

      // get all storyboard ids for new campaign
      await Promise.all(
        themeIds.map(themeId => {
          return this.CreativeInterface.getStoryboardList(themeId, ids => {
            storyboardIds.push(...ids)
          })
        })
      )

      // get all template ids for new campaign
      let newStoryboards = []
      await Promise.all(
        storyboardIds.map(sid => {
          return new Promise((resolve, reject) =>
            this.CreativeInterface.getStoryboard(sid, sb => {
              let newStoryboard = JSON.parse(JSON.stringify(sb))
              sb.properties.storyboardJson.templates.forEach(template => {
                if (!templateIds.some(tid => tid == template.templateID)) {
                  templateIds.push(template.templateID)
                }
              })

              newStoryboards.push(newStoryboard)
              resolve()
            }).catch(err => {
              reject(new Error(`Error fetching Storyboard Id ${sid}: ${err}`))
            })
          )
        })
      ).then(() => {
        this.currentProgress = 65
      })
      // upload assets to templates
      const templatePromiseFuncs = []
      const deletePromiseFuncs = []
      const temporaryTemplateIdsForDeletingAssets = {}
      newStoryboards.forEach(sb => {
        const storyboardToCopyDataFrom = this.storyboards.find(
          s => s.width === sb.width && s.height === sb.height
        )
        // use first template as global template for all templates in storyboard
        const templateToCopyDataFrom = storyboardToCopyDataFrom.templates[0]
        const temporaryTemplateId = templateToCopyDataFrom.templateId
        const filePath = AssetPathService.getQuickStartTemplateAssetPath(temporaryTemplateId).split(
          'assets/'
        )[1]
        const templateIdsToUpdate = sb.properties.storyboardJson.templates.map(t => t.templateID)
        // copy assets from temporary template data to new permanent template (copyAssetV3 requires filePath without assets/ prefix)
        templateToCopyDataFrom.blueprint.fontFaceSet.forEach(fontFace => {
          if (fontFace.src?.length && fontFace.src.includes('abstractQuickStartTemplatePath')) {
            const fileName = fontFace.src.split('/').pop()
            const templatePromiseFunc = () => {
              return new Promise(res => {
                this.CreativeInterface.copyAssetToTemplates({
                  filePath,
                  fileName,
                  templateIDs: templateIdsToUpdate,
                }).then(r => {
                  if (r && r.responseData) {
                    fontFace.src = r.responseData
                  }
                  res()
                })
              })
            }
            templatePromiseFuncs.push(templatePromiseFunc)
          }
        })
        templateToCopyDataFrom.blueprint.widgetGroups.forEach(wg => {
          wg.widgets.forEach(w => {
            if (w.type === 'image') {
              let fileName
              if (typeof w.value.content == 'object') {
                fileName = w.value.content.defaultValue.split('/').pop()
              } else {
                fileName = w.value.content.split('/').pop()
              }
              const templatePromiseFunc = () => {
                return new Promise(res => {
                  this.CreativeInterface.copyAssetToTemplates({
                    filePath,
                    fileName,
                    templateIDs: templateIdsToUpdate,
                  }).then(r => {
                    if (r && r.responseData) {
                      if (typeof w.value.content == 'object') {
                        w.value.content.defaultValue = r.responseData
                      } else {
                        w.value.content = r.responseData
                      }
                    }
                    res()
                  })
                })
              }
              templatePromiseFuncs.push(templatePromiseFunc)
              if (!temporaryTemplateIdsForDeletingAssets[temporaryTemplateId]) {
                const deletePromiseFunc = () => {
                  return new Promise(res => {
                    this.CreativeInterface.deleteQuickStartTemplateDirectory(
                      temporaryTemplateId
                    ).then(res)
                  })
                }
                deletePromiseFuncs.push(deletePromiseFunc)
                temporaryTemplateIdsForDeletingAssets[temporaryTemplateId] = true
              }
            }
            if (w.type === 'dotNavigation') {
              w.value.config.dotButtonCount = this.frameCount
            }
          })
        })
      })
      while (templatePromiseFuncs.length) {
        await Promise.all(templatePromiseFuncs.splice(0, 9).map(func => func()))
      }
      while (deletePromiseFuncs.length) {
        await Promise.all(deletePromiseFuncs.splice(0, 9).map(func => func()))
      }
      this.currentProgress = 80

      // apply blueprint to templates
      // get all template objects to assign assets to company logo and image widgets
      await Promise.all(
        templateIds.map(tid => {
          return new Promise((resolve, reject) => {
            this.CreativeInterface.getTemplate(tid, template => {
              if (!template.blueprint.widgetGroups) {
                template.blueprint.widgetGroups = [
                  {
                    widgets: template.blueprint.widgets,
                    properties: {},
                  },
                ]
                delete template.blueprint.widgets
              }
              const templateToCopyDataFrom = this.storyboards.find(
                s => s.width === template.width && s.height === template.height
              ).templates[0]
              template.blueprint = JSON.parse(JSON.stringify(templateToCopyDataFrom.blueprint))
              // export template blueprint as html
              template.properties.templateHtml = CanvasExporter.exportAsHTML(template.blueprint, {
                excludeOutofboundContent: true,
                clipImageWidgets: true,
                quickStart: { productCount: this.frameCount },
              })

              // collect all template update promises
              templateUpdatePromises.push(
                new Promise((res, rej) => {
                  this.CreativeInterface.updateTemplate(template)
                    .then(res)
                    .catch(err => rej(err))
                })
              )
              resolve()
            }).catch(err => {
              reject(new Error(`Error fetching Template Id ${tid}: ${err}`))
            })
          })
        })
      )
      // update all templates with new asset urls
      await Promise.all(templateUpdatePromises)

      this.currentProgress = 90
      if (
        isActivatingMediaChannel &&
        !isAdTagsUser &&
        // don't allow test accounts to push to Basis from the on-boarding flow,
        // if we need to test this, we can do it within the application
        !this.$firebaseUser.email.includes('@mailinator.com')
      ) {
        await this.MediaChannelInterface.updateStatus({
          mediaChannelId: dspMediaChannel.mediaChannelId,
          status: 'active',
        })
      }
      this.currentProgress = 100
      // default to sending users to the ad serving page (ie. save and continue later - you decide what's next)
      let postSaveRedirectPath = `/campaign-creative/v2/page/ai/${this.newAdvertiserId}/ci/${this.newCampaignId}/tn/campaignAdServing`
      // if user is using a 3rd-party dsp, then redirect to the ad tags config page specifically
      if (isAdTagsUser) {
        if (isOnboarding) {
          localStorage.setItem('adaIsRedirectToAdTagsFromOnBoarding', 'true')
        }
        let mediaChannelId = adTagsChannel ? adTagsChannel.mediaChannelId : undefined
        if (!mediaChannelId) {
          // if adtags channel doesn't exist at this point, just create one
          await this.MediaChannelInterface.createMediaChannel({
            campaignId: this.newCampaignId,
            type: 'adtags',
            properties: {},
            status: 'inactive',
            startDate: '1970-01-01',
            endDate: '1970-01-01',
          }).then(res => {
            mediaChannelId = res.responseData.changedKeys.find(key => key.keytype === 'mci')
              .keyvalue[0]
          })
        }
        postSaveRedirectPath = `/campaign-creative/v2/page/ai/${this.newAdvertiserId}/ci/${this.newCampaignId}/mci/${mediaChannelId}/tn/campaignAdServing/mo/adtags`
        this.mobileCompleteContent = 'adtags'
      } else if (isActivatingMediaChannel) {
        // if user is activating their basis ads, then redirect to the dashboard page
        localStorage.setItem('adaIsRedirectToDashboardFromQuickStart', 'true')
        postSaveRedirectPath = `/campaign-creative/v2/page/ai/${this.newAdvertiserId}/ci/${this.newCampaignId}/tn/campaignDashboard`
      } else {
        // save and continue later
        localStorage.setItem('adaIsRedirectToAdServingFromQuickStart', 'true')
        this.mobileCompleteContent = 'saveAndContinue'
      }
      await this.AccountInterface.get(async account => {
        account.Properties.applicationConfig = await ApplicationConfig.bootstrapInit(undefined)
        window.adacado.applicationConfig = account.Properties.applicationConfig
        account.Properties.applicationConfig = ApplicationConfig.set(
          'campaignGuide',
          'autoshow',
          false
        )
        await this.AccountInterface.update(account).catch(err => {
          //  On error... show nothing to user but log it
          console.error(
            'Header.HelpMenu:Error saving User Account after updating application config',
            err
          )
        })
      })
      // all done, remove progress data
      localStorage.removeItem('adaQuickStartData')
      // set the local storage values for the current user
      if (!this.accounts[this.$firebaseUser.email]) {
        this.accounts[this.$firebaseUser.email] = {}
      }
      this.accounts[this.$firebaseUser.email].currentAdvertiserId = this.newAdvertiserId
      this.accounts[this.$firebaseUser.email].currentCampaignId = this.newCampaignId
      localStorage.setItem('accounts', JSON.stringify(this.accounts))
      if (!isActivatingMediaChannel && !isAdTagsUser && !this.isMobileSized) {
        this.$nextTick(() => {
          this.currentProgress = 0
          this.progressIndicatorColor = 'teal'
          this.progressText = 'Generating thumbnails...'
          setInterval(() => {
            if (this.currentProgress >= 100) {
              window.location = postSaveRedirectPath
              return
            }
            this.currentProgress += 10
            if (this.currentProgress === 90) {
              this.progressText =
                'Your ads have been generated and are now ready to be served. You will be redirected to the Ad Serving page in just a moment.'
            }
          }, 1000)
        })
      } else {
        this.progressText = isActivatingMediaChannel
          ? 'Success! Your ads have been generated and are now being served.'
          : 'Your ads have been generated and are now ready to be served. You will be redirected to the Ad Serving page in just a moment.'
        if (this.isMobileSized) {
          this.currentStep = 'mobileComplete'
          if (this.mobileCompleteContent == 'analytics') {
            FemsService.renderChildFems('header', 'v2')
          }
        } else {
          setTimeout(() => {
            window.location = postSaveRedirectPath
          }, 3000)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.welcomeContainer {
  width: 100vw;
  margin-top: 0px;
  .welcomeContent {
    display: flex;
    height: 100vh;
    position: relative;
    width: 100%;
    margin: auto;
    align-items: center;
    .welcomeBox {
      display: flex;
      padding: 20px;
      width: 1600px;
      justify-content: space-between;
      align-items: center;
    }
  }
}

.unAuthLogo {
  position: absolute;
  top: -20px;
  left: 40px;
  img {
    width: 150px;
    height: 150px;
  }
  &.mobileLogo {
    width: 100%;
    background-color: white;
    z-index: 2;
    height: 104px;
    left: 0px;
    position: fixed;
  }
}
.adacadoLogoMobile {
  position: absolute;
  left: 10px;
}

.welcomeHeader {
  .welcomeTitle {
    font-family: 'Mulish' !important;
    font-weight: 800;
  }
  .welcomeTextMessage {
    font-family: 'Source Sans Pro' !important;
  }
}

.panel {
  width: 400px;
  height: 508px;
  min-width: 400px;
  text-align: center;
  * {
    transition: all 1.5s cubic-bezier(0.25, 0.46, 0, 0.99);
  }
}

.fadeInSlideUp-enter-active,
.fadeInSlideUp-leave-active {
  transition: all 1s cubic-bezier(0.25, 0.46, 0, 0.99);
  transform: translateY(0%);
}
.fadeInSlideUp-enter,
.fadeInSlideUp-leave-to {
  transform: translateY(10%);
  opacity: 0;
}

.authSVG {
  width: 14px;
  height: 14px;
  margin-right: 15px;
}

.footerWrapper {
  position: fixed;
  bottom: 0;
  width: 100%;
  padding: 0 0 5px 20px;
  &.mobileFooter {
    height: 146px !important;
    background-color: white;
    padding-bottom: 0px;
    bottom: 0dvh;
    z-index: 4;
  }
  &.mobileCompleteFooter {
    height: 85px !important;
    background-color: white;
    bottom: -6px;
  }
  .mobilePrivacyDisplay {
    width: 246px;
    position: absolute;
    bottom: 26px;
  }
}

#closeIcon {
  display: flex;
  justify-content: flex-end;
}

.modal-headline {
  justify-content: center;
}

.smallText {
  font-size: 14px;
}

.v-btn {
  letter-spacing: 0;
}

.forgotPasswordLink {
  cursor: pointer;
  color: #1976d2;
  text-decoration: underline;
}

@media (max-width: 1280px) {
  .container {
    max-width: 1076px;
  }
}

@media (max-width: 1086px) {
  .container {
    max-width: 928px;
  }
  .panel {
    width: 350px;
    min-width: 350px;
  }
}

@media (max-height: 785px) {
  .welcomeContainer .welcomeContent .welcomeBox {
    margin-top: 68px;
  }
}

.header {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1;
}
</style>
<style lang="scss">
.largeDescriptionText {
  font-size: 20px;
  font-weight: 500;
  color: #9e9e9e;
  &.mobileDescriptionText {
    font-size: 15px !important;
  }
}
</style>
